import { FC } from 'react';

import {
  getAssetPrecisions,
  getPrecisedNumber,
  getTxAmount,
  getUsdAmount,
} from '@/helpers';
import { useTranslation } from 'react-i18next';
import styles from './AmlCheckBalance.module.sass';

interface AmlCheckBalanceProps {
  data: Record<string, any> | null;
  asset: string;
  network: string;
}

const AmlCheckBalance: FC<AmlCheckBalanceProps> = ({
  data,
  asset,
  network,
}) => {
  const { t } = useTranslation();

  const [precision, viewPrecision] = getAssetPrecisions(asset, network);
  const balancePrecision =
    data &&
    Object.prototype.hasOwnProperty.call(data, 'reported_balance_precision')
      ? data.reported_balance_precision
      : precision;
  const balanceUsd = data?.balance_usd ? getUsdAmount(data.balance_usd) : 0;
  const balanceAsset =
    getPrecisedNumber(data?.balance_approximate, viewPrecision) ||
    (data?.balance
      ? getTxAmount(data.balance, balancePrecision, viewPrecision)
      : 0);
  const receivedUsd = data?.received_usd ? getUsdAmount(data.received_usd) : 0;
  const receivedAsset = data?.received
    ? getTxAmount(data.received, balancePrecision, viewPrecision)
    : 0;
  const spentUsd = data?.sent_usd ? getUsdAmount(data.sent_usd) : 0;
  const spentAsset = data?.sent
    ? getTxAmount(data.sent, balancePrecision, viewPrecision)
    : 0;

  const hasReceived = !!receivedUsd || !!receivedAsset;
  const hasSpent = !!spentUsd || !!spentAsset;

  return (
    <div className={styles.balance}>
      <div className={styles.balance__title}>
        {t('translation.balance_str')}
      </div>
      {!!balanceUsd && (
        <div className={styles.balance__value}>{balanceUsd}</div>
      )}
      {!!balanceAsset && (
        <div className={styles.balance__crypto}>
          {balanceAsset} {asset}
        </div>
      )}
      <table className={styles.balance__list}>
        <tbody>
          {hasReceived && (
            <tr>
              <td>{t('translation.account_received_str')}</td>
              <td>
                {!!receivedUsd && (
                  <>
                    <span>{receivedUsd}</span>&nbsp;•&nbsp;
                  </>
                )}
                {!!receivedAsset && (
                  <span>
                    {receivedAsset} {asset}
                  </span>
                )}
              </td>
            </tr>
          )}

          {!!hasSpent && (
            <tr>
              <td>{t('translation.account_sent_str')}</td>
              <td>
                {!!spentUsd && (
                  <>
                    <span>{spentUsd}</span>&nbsp;•&nbsp;
                  </>
                )}
                {!!spentAsset && (
                  <span>
                    {spentAsset} {asset}
                  </span>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AmlCheckBalance;
